import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError, fmtDate } from "@/lib/api";

const EMPTY = { code: "", type: "pourcentage", value: "", expires_at_local: "", max_uses: "", active: true };

const PromoForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const editing = !!initial.created_at;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, value: Number(f.value), max_uses: f.max_uses === "" || f.max_uses == null ? null : Number(f.max_uses), expires_at_local: f.expires_at_local || null };
    try {
      editing ? await adminApi.put(`/promos/${f.code}`, body) : await adminApi.post("/promos", body);
      toast.success(editing ? "Code mis à jour" : "Code créé");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <form onSubmit={submit} data-testid="promo-form" className="order-card p-5 grid sm:grid-cols-3 gap-4 items-end">
      <label className="flex flex-col gap-1.5 text-sm font-semibold">Code
        <input data-testid="promo-form-code" value={f.code} disabled={editing} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="field-input uppercase" required />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-semibold">Type
        <select data-testid="promo-form-type" value={f.type} onChange={set("type")} className="field-input">
          <option value="pourcentage">Pourcentage (%)</option>
          <option value="montant">Montant fixe (€)</option>
        </select>
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-semibold">Valeur
        <input data-testid="promo-form-value" type="number" step="0.01" min="0" value={f.value} onChange={set("value")} className="field-input" required />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-semibold">Expiration (heure de Paris, facultatif)
        <input data-testid="promo-form-expires" type="datetime-local" value={f.expires_at_local || ""} onChange={set("expires_at_local")} className="field-input" />
      </label>
      <label className="flex flex-col gap-1.5 text-sm font-semibold">Utilisations max (facultatif)
        <input data-testid="promo-form-max-uses" type="number" min="1" value={f.max_uses ?? ""} onChange={set("max_uses")} className="field-input" />
      </label>
      <label className="flex items-center gap-2 text-sm font-semibold pb-3">
        <input data-testid="promo-form-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4" /> Actif
      </label>
      <div className="sm:col-span-3 flex gap-3">
        <button type="submit" data-testid="promo-form-submit" className="btn-heat !py-2.5">{editing ? "Enregistrer" : "Créer le code"}</button>
        <button type="button" onClick={onCancel} className="btn-ghost-ice !py-2.5">Annuler</button>
      </div>
    </form>
  );
};

export const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => adminApi.get("/promos").then(({ data }) => setPromos(data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    try {
      await adminApi.put(`/promos/${p.code}`, { ...p, active: !p.active });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {editing ? (
        <PromoForm key={editing.code || "new"} initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <button type="button" onClick={() => setEditing(EMPTY)} data-testid="promo-new-button" className="btn-heat !py-2.5 self-start">Nouveau code promo</button>
      )}
      <div className="admin-table-wrap">
        <table data-testid="promos-table" className="admin-table">
          <thead><tr><th>Code</th><th>Remise</th><th>Expiration</th><th>Utilisations (payées)</th><th>Statut</th><th /></tr></thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.code} data-testid={`promo-row-${p.code}`}>
                <td className="font-mono font-semibold">{p.code}</td>
                <td>{p.type === "pourcentage" ? `−${p.value} %` : `−${p.value} €`}</td>
                <td>{p.expires_at ? fmtDate(p.expires_at) : "—"}</td>
                <td data-testid={`promo-uses-${p.code}`}>{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                <td>
                  <button type="button" onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                    {p.active ? "Actif" : "Inactif"}
                  </button>
                </td>
                <td><button type="button" onClick={() => setEditing(p)} data-testid={`promo-edit-${p.code}`} className="text-sky-700 font-semibold text-sm">Modifier</button></td>
              </tr>
            ))}
            {promos.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-10">Aucun code promo.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
